// clientDataValidator.js - Safe data analysis tool (no modifications)
// This utility analyzes your client data and provides recommendations without changing anything

export class ClientDataValidator {
  constructor(clients) {
    this.clients = clients || [];
  }

  // Comprehensive data quality analysis
  analyzeDataQuality() {
    const analysis = {
      totalClients: this.clients.length,
      issues: {
        missingNames: [],
        missingContact: [],
        missingAddress: [],
        invalidEmails: [],
        invalidPhones: [],
        invalidWebsites: [],
        incompleteProfiles: [],
        potentialDuplicates: []
      },
      recommendations: []
    };

    this.clients.forEach(client => {
      // Check for missing critical information
      if (!client.name || client.name.trim() === '') {
        analysis.issues.missingNames.push({
          id: client.id,
          issue: 'Missing or empty name'
        });
      }

      // Check contact information
      if (!client.phone && !client.email) {
        analysis.issues.missingContact.push({
          id: client.id,
          name: client.name,
          issue: 'No phone or email provided'
        });
      }

      // Check address completeness
      if (!client.street1 || !client.city || !client.state) {
        analysis.issues.missingAddress.push({
          id: client.id,
          name: client.name,
          missing: [
            !client.street1 ? 'street' : null,
            !client.city ? 'city' : null,
            !client.state ? 'state' : null
          ].filter(Boolean)
        });
      }

      // Validate email format
      if (client.email && !this.isValidEmail(client.email)) {
        analysis.issues.invalidEmails.push({
          id: client.id,
          name: client.name,
          email: client.email
        });
      }
      if (client.email2 && !this.isValidEmail(client.email2)) {
        analysis.issues.invalidEmails.push({
          id: client.id,
          name: client.name,
          email: client.email2
        });
      }

      // Validate phone format
      if (client.phone && !this.isValidPhone(client.phone)) {
        analysis.issues.invalidPhones.push({
          id: client.id,
          name: client.name,
          phone: client.phone
        });
      }

      // Validate website URLs
      if (client.dealershipWebsite && !this.isValidWebsite(client.dealershipWebsite)) {
        analysis.issues.invalidWebsites.push({
          id: client.id,
          name: client.name,
          website: client.dealershipWebsite
        });
      }

      // Calculate profile completeness
      const completeness = this.calculateCompleteness(client);
      if (completeness < 70) {
        analysis.issues.incompleteProfiles.push({
          id: client.id,
          name: client.name,
          completeness: completeness,
          missingFields: this.getMissingFields(client)
        });
      }
    });

    // Find potential duplicates
    analysis.issues.potentialDuplicates = this.findDuplicates();

    // Generate recommendations
    analysis.recommendations = this.generateRecommendations(analysis.issues);

    return analysis;
  }

  // Email validation
  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  }

  // Phone validation (flexible format)
  isValidPhone(phone) {
    const cleanPhone = phone.replace(/\D/g, '');
    return cleanPhone.length >= 10 && cleanPhone.length <= 11;
  }

  // Website validation
  isValidWebsite(website) {
    try {
      const url = new URL(website.startsWith('http') ? website : `https://${website}`);
      return ['http:', 'https:'].includes(url.protocol);
    } catch {
      return false;
    }
  }

  // Calculate profile completeness percentage
  calculateCompleteness(client) {
    const requiredFields = [
      'name', 'phone', 'email', 'street1', 'city', 'state', 
      'zip', 'manufacturer', 'generalManager'
    ];
    
    const optionalFields = ['phone2', 'email2', 'dealershipWebsite', 'dealerAppsCode'];
    
    const requiredScore = requiredFields.filter(field => 
      client[field] && client[field].toString().trim() !== ''
    ).length / requiredFields.length * 80; // 80% of total score
    
    const optionalScore = optionalFields.filter(field => 
      client[field] && client[field].toString().trim() !== ''
    ).length / optionalFields.length * 20; // 20% of total score
    
    return Math.round(requiredScore + optionalScore);
  }

  // Get list of missing fields
  getMissingFields(client) {
    const allFields = [
      { name: 'name', label: 'Business Name', required: true },
      { name: 'phone', label: 'Primary Phone', required: true },
      { name: 'email', label: 'Primary Email', required: true },
      { name: 'street1', label: 'Street Address', required: true },
      { name: 'city', label: 'City', required: true },
      { name: 'state', label: 'State', required: true },
      { name: 'zip', label: 'ZIP Code', required: true },
      { name: 'manufacturer', label: 'Manufacturer', required: true },
      { name: 'generalManager', label: 'General Manager', required: true },
      { name: 'phone2', label: 'Secondary Phone', required: false },
      { name: 'email2', label: 'Secondary Email', required: false },
      { name: 'dealershipWebsite', label: 'Website', required: false },
      { name: 'dealerAppsCode', label: 'Dealer Apps Code', required: false }
    ];

    return allFields.filter(field => {
      const value = client[field.name];
      return !value || value.toString().trim() === '';
    });
  }

  // Find potential duplicate clients
  findDuplicates() {
    const duplicates = [];
    
    for (let i = 0; i < this.clients.length; i++) {
      for (let j = i + 1; j < this.clients.length; j++) {
        const client1 = this.clients[i];
        const client2 = this.clients[j];
        const matches = this.compareClients(client1, client2);
        
        if (matches.score > 0) {
          duplicates.push({
            client1: { id: client1.id, name: client1.name },
            client2: { id: client2.id, name: client2.name },
            matchReasons: matches.reasons,
            confidence: matches.score
          });
        }
      }
    }
    
    return duplicates.sort((a, b) => b.confidence - a.confidence);
  }

  // Compare two clients for similarity
  compareClients(client1, client2) {
    const matches = { score: 0, reasons: [] };
    
    // Exact name match
    const name1 = (client1.name || '').toLowerCase().trim();
    const name2 = (client2.name || '').toLowerCase().trim();
    if (name1 && name2 && name1 === name2) {
      matches.score += 50;
      matches.reasons.push('Identical names');
    }
    
    // Similar name match (fuzzy)
    if (name1 && name2 && this.calculateSimilarity(name1, name2) > 0.8) {
      matches.score += 30;
      matches.reasons.push('Very similar names');
    }
    
    // Phone number match
    const phone1 = (client1.phone || '').replace(/\D/g, '');
    const phone2 = (client2.phone || '').replace(/\D/g, '');
    if (phone1 && phone2 && phone1.length > 6 && phone1 === phone2) {
      matches.score += 40;
      matches.reasons.push('Same phone number');
    }
    
    // Email match
    const email1 = (client1.email || '').toLowerCase().trim();
    const email2 = (client2.email || '').toLowerCase().trim();
    if (email1 && email2 && email1 === email2) {
      matches.score += 45;
      matches.reasons.push('Same email address');
    }
    
    // Address match
    const addr1 = `${client1.street1 || ''} ${client1.city || ''} ${client1.state || ''}`.toLowerCase().trim();
    const addr2 = `${client2.street1 || ''} ${client2.city || ''} ${client2.state || ''}`.toLowerCase().trim();
    if (addr1 && addr2 && addr1 === addr2) {
      matches.score += 35;
      matches.reasons.push('Same address');
    }
    
    return matches;
  }

  // Calculate string similarity (Levenshtein distance based)
  calculateSimilarity(str1, str2) {
    const longer = str1.length > str2.length ? str1 : str2;
    const shorter = str1.length > str2.length ? str2 : str1;
    
    if (longer.length === 0) return 1.0;
    
    const distance = this.levenshteinDistance(longer, shorter);
    return (longer.length - distance) / longer.length;
  }

  // Levenshtein distance calculation
  levenshteinDistance(str1, str2) {
    const matrix = [];
    
    for (let i = 0; i <= str2.length; i++) {
      matrix[i] = [i];
    }
    
    for (let j = 0; j <= str1.length; j++) {
      matrix[0][j] = j;
    }
    
    for (let i = 1; i <= str2.length; i++) {
      for (let j = 1; j <= str1.length; j++) {
        if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1
          );
        }
      }
    }
    
    return matrix[str2.length][str1.length];
  }

  // Generate actionable recommendations
  generateRecommendations(issues) {
    const recommendations = [];
    
    // Critical issues first
    if (issues.missingNames.length > 0) {
      recommendations.push({
        priority: 'high',
        category: 'Critical Data',
        title: `Fix ${issues.missingNames.length} clients with missing names`,
        description: 'Clients without names cannot be properly identified or contacted.',
        action: 'Review and add business names for all unnamed clients.',
        clients: issues.missingNames.map(item => item.id)
      });
    }
    
    if (issues.missingContact.length > 0) {
      recommendations.push({
        priority: 'high',
        category: 'Contact Information',
        title: `Add contact info for ${issues.missingContact.length} clients`,
        description: 'These clients have no phone number or email address.',
        action: 'Obtain at least one form of contact information (phone or email).',
        clients: issues.missingContact.map(item => item.id)
      });
    }
    
    // Data quality issues
    if (issues.invalidEmails.length > 0) {
      recommendations.push({
        priority: 'medium',
        category: 'Data Quality',
        title: `Fix ${issues.invalidEmails.length} invalid email addresses`,
        description: 'These email addresses appear to be incorrectly formatted.',
        action: 'Verify and correct email address formats.',
        clients: issues.invalidEmails.map(item => item.id)
      });
    }
    
    if (issues.invalidPhones.length > 0) {
      recommendations.push({
        priority: 'medium',
        category: 'Data Quality',
        title: `Fix ${issues.invalidPhones.length} invalid phone numbers`,
        description: 'These phone numbers appear to be incorrectly formatted.',
        action: 'Verify and correct phone number formats.',
        clients: issues.invalidPhones.map(item => item.id)
      });
    }
    
    // Completeness issues
    if (issues.incompleteProfiles.length > 0) {
      recommendations.push({
        priority: 'medium',
        category: 'Profile Completeness',
        title: `Complete ${issues.incompleteProfiles.length} incomplete profiles`,
        description: 'These profiles are missing important information.',
        action: 'Fill in missing required fields to improve data quality.',
        clients: issues.incompleteProfiles.map(item => item.id)
      });
    }
    
    // Duplicate handling
    if (issues.potentialDuplicates.length > 0) {
      recommendations.push({
        priority: 'medium',
        category: 'Data Integrity',
        title: `Review ${issues.potentialDuplicates.length} potential duplicate pairs`,
        description: 'These clients may be duplicate entries.',
        action: 'Review each pair and merge or differentiate as appropriate.',
        duplicatePairs: issues.potentialDuplicates.length
      });
    }
    
    // Address completeness
    if (issues.missingAddress.length > 0) {
      recommendations.push({
        priority: 'low',
        category: 'Address Information',
        title: `Complete address info for ${issues.missingAddress.length} clients`,
        description: 'These clients have incomplete address information.',
        action: 'Add missing street, city, or state information.',
        clients: issues.missingAddress.map(item => item.id)
      });
    }
    
    return recommendations.sort((a, b) => {
      const priorityOrder = { high: 3, medium: 2, low: 1 };
      return priorityOrder[b.priority] - priorityOrder[a.priority];
    });
  }

  // Generate a summary report
  generateReport() {
    const analysis = this.analyzeDataQuality();
    
    const report = {
      summary: {
        totalClients: analysis.totalClients,
        overallQuality: this.calculateOverallQuality(analysis),
        criticalIssues: this.countCriticalIssues(analysis.issues),
        completionRate: this.calculateCompletionRate()
      },
      analysis,
      actionPlan: this.createActionPlan(analysis.recommendations)
    };
    
    return report;
  }

  // Calculate overall data quality score
  calculateOverallQuality(analysis) {
    const totalIssues = Object.values(analysis.issues).reduce((sum, issues) => {
      return sum + (Array.isArray(issues) ? issues.length : 0);
    }, 0);
    
    const maxPossibleIssues = analysis.totalClients * 5; // Rough estimate
    const qualityScore = Math.max(0, Math.round((1 - totalIssues / maxPossibleIssues) * 100));
    
    return {
      score: qualityScore,
      grade: qualityScore >= 90 ? 'A' : qualityScore >= 80 ? 'B' : qualityScore >= 70 ? 'C' : qualityScore >= 60 ? 'D' : 'F'
    };
  }

  // Count critical issues that need immediate attention
  countCriticalIssues(issues) {
    return issues.missingNames.length + issues.missingContact.length;
  }

  // Calculate average profile completion rate
  calculateCompletionRate() {
    if (this.clients.length === 0) return 0;
    
    const totalCompleteness = this.clients.reduce((sum, client) => {
      return sum + this.calculateCompleteness(client);
    }, 0);
    
    return Math.round(totalCompleteness / this.clients.length);
  }

  // Create actionable plan with timeline
  createActionPlan(recommendations) {
    const plan = {
      immediate: recommendations.filter(r => r.priority === 'high'),
      shortTerm: recommendations.filter(r => r.priority === 'medium'),
      longTerm: recommendations.filter(r => r.priority === 'low'),
      estimatedTimeToComplete: this.estimateCompletionTime(recommendations)
    };
    
    return plan;
  }

  // Estimate time needed to complete all recommendations
  estimateCompletionTime(recommendations) {
    const timeEstimates = {
      high: 30, // minutes per issue
      medium: 15,
      low: 10
    };
    
    const totalMinutes = recommendations.reduce((total, rec) => {
      const clientCount = rec.clients ? rec.clients.length : rec.duplicatePairs || 1;
      return total + (timeEstimates[rec.priority] * clientCount);
    }, 0);
    
    const hours = Math.round(totalMinutes / 60 * 10) / 10;
    return {
      totalMinutes,
      hours,
      estimatedDays: Math.ceil(hours / 8) // Assuming 8 hour work days
    };
  }
}

// Export utility functions for use in components
export const validateClientData = (clients) => {
  const validator = new ClientDataValidator(clients);
  return validator.generateReport();
};

export const findClientDuplicates = (clients) => {
  const validator = new ClientDataValidator(clients);
  return validator.findDuplicates();
};

export const calculateClientQuality = (client) => {
  const validator = new ClientDataValidator([client]);
  return validator.calculateCompleteness(client);
};